import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { toPageError } from '../partners/PartnerListPage';
import { ReportDrilldowns } from '../reports/ReportDrilldowns';
import { AgentFormDialog } from './AgentFormDialog';
import { AgentStatusChip } from './AgentListPage';
import { AgentStatusDialog } from './AgentStatusDialog';
import { DefaultUplineDialog } from './DefaultUplineDialog';
import { agentRepositoryToken } from './repository';
import type { AgentDetail } from './types';

export function AgentDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { agentId = '' } = useParams();
  const repository = useRepository(agentRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [uplineOpen, setUplineOpen] = useState(false);
  const [houseOpen, setHouseOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.get(agentId, signal),
    [agentId, refreshKey, repository],
    () => false,
  );
  const principal = auth.status === 'authenticated' ? auth.principal : null;
  const canManage = principal?.permissions.includes('AGENT_MANAGE') === true;
  const canCreateHouse = principal?.permissions.includes('MEMBER_MANAGE') === true;
  const refresh = () => setRefreshKey((key) => key + 1);

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toPageError(resource.error, t('agents.errors.detail'))}
        onRetry={refresh}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <AgentDetailContent
            agent={resource.data}
            canCreateHouse={canCreateHouse}
            canManage={canManage}
            dataSource={dataSource}
            language={language}
            onEdit={() => setEditOpen(true)}
            onHouse={() => setHouseOpen(true)}
            onStatus={() => setStatusOpen(true)}
            onUpline={() => setUplineOpen(true)}
          />
        )}
      </PageState>
      {resource.data === null ? null : (
        <>
          <AgentFormDialog
            agent={resource.data}
            onClose={() => setEditOpen(false)}
            onSaved={refresh}
            open={editOpen}
          />
          <DefaultUplineDialog
            agent={resource.data}
            onClose={() => setUplineOpen(false)}
            onSaved={refresh}
            open={uplineOpen}
          />
          <HouseMemberDialog
            agent={resource.data}
            onClose={() => setHouseOpen(false)}
            open={houseOpen}
          />
          <AgentStatusDialog
            agent={resource.data}
            onClose={() => setStatusOpen(false)}
            onSaved={refresh}
            open={statusOpen}
          />
        </>
      )}
    </Box>
  );
}

function AgentDetailContent({
  agent,
  canCreateHouse,
  canManage,
  dataSource,
  language,
  onEdit,
  onHouse,
  onStatus,
  onUpline,
}: {
  readonly agent: AgentDetail;
  readonly canCreateHouse: boolean;
  readonly canManage: boolean;
  readonly dataSource: DataSource;
  readonly language: TimeLanguage;
  readonly onEdit: () => void;
  readonly onHouse: () => void;
  readonly onStatus: () => void;
  readonly onUpline: () => void;
}) {
  const { t } = useTranslation();
  const created = formatTimestamp(agent.createdAt, 'Asia/Bangkok', language);
  const updated = formatTimestamp(agent.updatedAt, 'Asia/Bangkok', language);
  return (
    <Stack spacing={2}>
      <DetailHeader
        actions={
          canManage || canCreateHouse ? (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              sx={{ alignItems: { md: 'center' } }}
            >
              {canManage ? (
                <>
                  <Button onClick={onEdit} variant="outlined">
                    {t('agents.actions.edit')}
                  </Button>
                  <Button onClick={onUpline} variant="outlined">
                    {t('agents.actions.changeUpline')}
                  </Button>
                  <Button
                    color="warning"
                    disabled={agent.status === 'CLOSED'}
                    onClick={onStatus}
                    variant="contained"
                  >
                    {t('agents.actions.changeStatus')}
                  </Button>
                </>
              ) : null}
              {canCreateHouse ? (
                <Button onClick={onHouse} variant="outlined">
                  {t('agents.actions.createHouse')}
                </Button>
              ) : null}
            </Stack>
          ) : undefined
        }
        backTo={paths.agents}
        dataSource={dataSource}
        subtitle={agent.name}
        title={agent.code}
      />
      <ReportDrilldowns
        metrics={[
          {
            kind: 'transactions',
            filters: { partnerId: agent.partnerId, agentId: agent.id },
            label: t('reportSummaries.metrics.transactions'),
            value: null,
          },
          {
            kind: 'transactions',
            filters: { partnerId: agent.partnerId, agentId: agent.id },
            label: t('reportSummaries.metrics.actualPlay'),
            value: null,
          },
          {
            kind: 'agent-commissions',
            filters: { partnerId: agent.partnerId, agentId: agent.id },
            label: t('reportSummaries.metrics.commission'),
            value: null,
          },
        ]}
      />
      <Paper component="section" variant="outlined" sx={{ p: 2 }}>
        <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
          {t('agents.detail.overview')}
        </Typography>
        <MetadataList
          items={[
            {
              label: t('agents.fields.partner'),
              value: (
                <MuiLink component={Link} to={`/partners/${agent.partnerId}`}>
                  {agent.partnerId}
                </MuiLink>
              ),
            },
            { label: t('agents.fields.name'), value: agent.name },
            { label: t('agents.fields.status'), value: <AgentStatusChip status={agent.status} /> },
            { label: t('agents.fields.currency'), value: agent.defaultCurrencyCode },
            { label: t('agents.fields.userCount'), value: agent.userCount },
            { label: t('agents.fields.activeUserCount'), value: agent.activeUserCount },
            {
              label: t('agents.fields.defaultUpline'),
              value:
                agent.defaultUpline === null
                  ? t('common.notAvailable')
                  : `${agent.defaultUpline.memberId} · ${agent.defaultUpline.username ?? t('common.notAvailable')}`,
            },
            {
              label: t('agents.fields.contact'),
              value: (
                <Box
                  component="pre"
                  sx={{ m: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
                >
                  {JSON.stringify(agent.contact, null, 2)}
                </Box>
              ),
            },
            {
              label: t('agents.fields.createdAt'),
              value: `${created.display} (${created.originalOffset})`,
            },
            {
              label: t('agents.fields.updatedAt'),
              value: `${updated.display} (${updated.originalOffset})`,
            },
          ]}
        />
      </Paper>
      <Paper
        component="nav"
        aria-label={t('agents.detail.related')}
        variant="outlined"
        sx={{ p: 2 }}
      >
        <Typography component="h2" variant="h6">
          {t('agents.detail.related')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          <MuiLink component={Link} to={`/partners/${agent.partnerId}`}>
            {t('agents.detail.partner')}
          </MuiLink>
          <MuiLink component={Link} to={`${paths.members}?agentId=${encodeURIComponent(agent.id)}`}>
            {t('agents.detail.members')}
          </MuiLink>
          <MuiLink
            component={Link}
            to={`${paths.transactions}?agentId=${encodeURIComponent(agent.id)}`}
          >
            {t('agents.detail.transactions')}
          </MuiLink>
          <MuiLink
            component={Link}
            to={`${paths.commissionLedger}?agentId=${encodeURIComponent(agent.id)}`}
          >
            {t('agents.detail.commissionReports')}
          </MuiLink>
        </Stack>
      </Paper>
    </Stack>
  );
}

function HouseMemberDialog({
  agent,
  onClose,
  open,
}: {
  readonly agent: AgentDetail;
  readonly onClose: () => void;
  readonly open: boolean;
}) {
  const repository = useRepository(agentRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<{
    readonly username: string;
    readonly partnerUserId: string;
    readonly reason: string;
  }>({
    defaultValues: { username: '', partnerUserId: '', reason: '' },
  });
  const close = () => {
    if (guard.current) return;
    reset();
    setError(null);
    onClose();
  };
  const submit = handleSubmit(async (values) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      await repository.createHouseMember(agent.id, {
        username: values.username.trim(),
        ...(values.partnerUserId.trim().length === 0
          ? {}
          : { partnerUserId: values.partnerUserId.trim() }),
        reason: values.reason.trim(),
      });
      closeAfterSuccess(reset, onClose);
    } catch (submitError) {
      setError(toDialogError(submitError, t('agents.errors.houseMember')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog fullWidth onClose={close} open={open}>
      <DialogTitle>{t('agents.house.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="house-member-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">{t('agents.house.impact', { code: agent.code })}</Alert>
          <TextField
            error={errors.username !== undefined}
            helperText={errors.username?.message}
            label={t('agents.fields.username')}
            required
            {...register('username', {
              required: t('agents.validation.usernameRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('agents.validation.usernameRequired'),
            })}
          />
          <TextField label={t('agents.fields.partnerUserId')} {...register('partnerUserId')} />
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('agents.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('agents.validation.reasonRequired'),
              validate: (value) => value.trim().length > 0 || t('agents.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="house-member-form" loading={busy} type="submit" variant="contained">
          {t('actions.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function closeAfterSuccess(reset: () => void, onClose: () => void): void {
  reset();
  onClose();
}
